/**
 * useCopilot hook — encapsulates all AI Co-pilot API calls.
 *
 * Endpoints wired:
 * - POST /intelligence/run_engine_whatif — accepts scenario params, returns whatif result + diff_vs_current
 * - GET /intelligence/get_engine_run_diff?run_a=&run_b= — compare two engine runs
 * - GET /intelligence/prewarroom_scenarios — pre-war-room scenario envelope list
 * - GET /outcomes/engine-accuracy — per-engine prediction accuracy metrics
 *
 * Design constraints:
 * - No caching (exploratory/non-persisting by design)
 * - Returns loading/error states for each operation
 * - All money values returned as raw numbers for FinancialValue component rendering
 */

import { useState, useCallback } from 'react';
import { getAuthToken } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// TYPES
// =============================================================================

export interface WhatIfOverrides {
  [fieldId: string]: number | string | boolean | null;
}

export interface WhatIfRequest {
  reportId: string;
  engineId: string;
  overrides?: WhatIfOverrides;
}

export interface DiffResult {
  delta: number;
  pct_change: number;
  before: number;
  after: number;
}

export interface DiffVsCurrent {
  [fieldPath: string]: DiffResult | unknown;
}

export interface WhatIfResult {
  engine_id: string;
  overrides_applied: WhatIfOverrides;
  outputs: Record<string, unknown>;
  error: string | null;
  duration_ms: number;
  persisted: boolean;
  diff_vs_current: DiffVsCurrent | null;
  note?: string;
}

export interface EngineRunDiffRequest {
  reportId: string;
  engineId: string;
  runIdBefore?: string;
  runIdAfter?: string;
}

export interface EngineRunDiffResult {
  engine_id: string;
  report_id: string;
  run_before?: string;
  run_after?: string;
  diff: Record<string, DiffResult | unknown>;
  count: number;
}

export interface Scenario {
  id: string;
  name: string;
  description?: string;
  scenario_type: 'baseline' | 'conservative' | 'aggressive' | 'custom';
  overrides: WhatIfOverrides;
  projected_total?: number;
  diff_vs_baseline?: number;
}

export interface PrewarroomScenariosResult {
  report_id: string;
  baseline_available: boolean;
  baseline_total?: number;
  scenarios: Scenario[];
  generated_at: string;
  note?: string;
}

export interface EngineAccuracyMetrics {
  engine_id: string;
  engine_name?: string;
  total_predictions: number;
  mean_absolute_variance: number;
  mean_signed_variance: number;
  closest_prediction_rate: number;
  accuracy_score?: number;
}

export interface EngineAccuracyResult {
  engines: EngineAccuracyMetrics[];
  overall: {
    total_settlements: number;
    mean_accuracy: number;
  };
  note?: string;
}

// =============================================================================
// HOOK
// =============================================================================

interface UseCopilotState {
  whatIfResult: WhatIfResult | null;
  engineDiffResult: EngineRunDiffResult | null;
  scenariosResult: PrewarroomScenariosResult | null;
  accuracyResult: EngineAccuracyResult | null;
  isLoadingWhatIf: boolean;
  isLoadingDiff: boolean;
  isLoadingScenarios: boolean;
  isLoadingAccuracy: boolean;
  error: Error | null;
}

export function useCopilot() {
  const [state, setState] = useState<UseCopilotState>({
    whatIfResult: null,
    engineDiffResult: null,
    scenariosResult: null,
    accuracyResult: null,
    isLoadingWhatIf: false,
    isLoadingDiff: false,
    isLoadingScenarios: false,
    isLoadingAccuracy: false,
    error: null,
  });

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  /**
   * POST /intelligence/run_engine_whatif
   * Run an exploratory engine what-if scenario (NOT persisted)
   */
  const runEngineWhatIf = useCallback(async (
    request: WhatIfRequest
  ): Promise<WhatIfResult | null> => {
    setState((prev) => ({
      ...prev,
      isLoadingWhatIf: true,
      error: null,
    }));

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${BACKEND_URL}/api/v1/intelligence/run_engine_whatif`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            report_id: request.reportId,
            engine_id: request.engineId,
            overrides: request.overrides || {},
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `What-if failed: ${response.statusText}`
        );
      }

      const result: WhatIfResult = await response.json();
      setState((prev) => ({
        ...prev,
        whatIfResult: result,
        isLoadingWhatIf: false,
      }));
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setState((prev) => ({
        ...prev,
        error,
        isLoadingWhatIf: false,
      }));
      return null;
    }
  }, []);

  /**
   * GET /intelligence/get_engine_run_diff?run_a=&run_b=
   * Compare two engine runs (or latest two if no run IDs provided)
   */
  const getEngineRunDiff = useCallback(async (
    request: EngineRunDiffRequest
  ): Promise<EngineRunDiffResult | null> => {
    setState((prev) => ({
      ...prev,
      isLoadingDiff: true,
      error: null,
    }));

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams();
      if (request.runIdBefore) params.set('run_a', request.runIdBefore);
      if (request.runIdAfter) params.set('run_b', request.runIdAfter);
      params.set('engine_id', request.engineId);
      params.set('report_id', request.reportId);

      const response = await fetch(
        `${BACKEND_URL}/api/v1/intelligence/get_engine_run_diff?${params}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Diff fetch failed: ${response.statusText}`
        );
      }

      const result: EngineRunDiffResult = await response.json();
      setState((prev) => ({
        ...prev,
        engineDiffResult: result,
        isLoadingDiff: false,
      }));
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setState((prev) => ({
        ...prev,
        error,
        isLoadingDiff: false,
      }));
      return null;
    }
  }, []);

  /**
   * GET /intelligence/prewarroom_scenarios
   * Get pre-war-room scenario envelope list
   */
  const getPrewarroomScenarios = useCallback(async (
    reportId: string
  ): Promise<PrewarroomScenariosResult | null> => {
    setState((prev) => ({
      ...prev,
      isLoadingScenarios: true,
      error: null,
    }));

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${BACKEND_URL}/api/v1/intelligence/prewarroom_scenarios?report_id=${encodeURIComponent(reportId)}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Scenarios fetch failed: ${response.statusText}`
        );
      }

      const result: PrewarroomScenariosResult = await response.json();
      setState((prev) => ({
        ...prev,
        scenariosResult: result,
        isLoadingScenarios: false,
      }));
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setState((prev) => ({
        ...prev,
        error,
        isLoadingScenarios: false,
      }));
      return null;
    }
  }, []);

  /**
   * GET /outcomes/engine-accuracy
   * Get per-engine prediction accuracy metrics
   */
  const getEngineAccuracy = useCallback(async (
    limit?: number
  ): Promise<EngineAccuracyResult | null> => {
    setState((prev) => ({
      ...prev,
      isLoadingAccuracy: true,
      error: null,
    }));

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));

      const response = await fetch(
        `${BACKEND_URL}/api/v1/outcomes/engine-accuracy?${params}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Accuracy fetch failed: ${response.statusText}`
        );
      }

      const result: EngineAccuracyResult = await response.json();
      setState((prev) => ({
        ...prev,
        accuracyResult: result,
        isLoadingAccuracy: false,
      }));
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setState((prev) => ({
        ...prev,
        error,
        isLoadingAccuracy: false,
      }));
      return null;
    }
  }, []);

  return {
    // Results
    whatIfResult: state.whatIfResult,
    engineDiffResult: state.engineDiffResult,
    scenariosResult: state.scenariosResult,
    accuracyResult: state.accuracyResult,
    // Loading states
    isLoadingWhatIf: state.isLoadingWhatIf,
    isLoadingDiff: state.isLoadingDiff,
    isLoadingScenarios: state.isLoadingScenarios,
    isLoadingAccuracy: state.isLoadingAccuracy,
    isLoading: state.isLoadingWhatIf || state.isLoadingDiff || state.isLoadingScenarios || state.isLoadingAccuracy,
    // Error
    error: state.error,
    clearError,
    // Actions
    runEngineWhatIf,
    getEngineRunDiff,
    getPrewarroomScenarios,
    getEngineAccuracy,
  };
}

export default useCopilot;
